import { useState, useCallback, useEffect } from 'react';
import { useAppDispatch } from '../store/hooks';
import { AsyncThunk } from '@reduxjs/toolkit';

interface UseTableStateOptions {
  fetchAction: AsyncThunk<any, any, any>;
  initialPage?: number;
  additionalParams?: Record<string, any>;
  autoFetch?: boolean;
}

interface UseTableStateReturn {
  currentPage: number;
  sortValue: string;
  sortOrder: number | '';
  changePage: (page: number, pageSize?: number) => void;
  sortData: (sortVal: string) => void;
  refreshData: () => void;
  setCurrentPage: (page: number) => void;
}

/**
 * Hook to manage table pagination and sorting state
 * Automatically dispatches fetch action when page or sort changes
 *
 * @param options - Configuration options
 * @param options.fetchAction - Redux async thunk to dispatch for fetching data
 * @param options.initialPage - Initial page number (default: 0)
 * @param options.additionalParams - Additional params to include in fetch requests
 * @param options.autoFetch - Whether to fetch data on mount (default: true)
 *
 * @example
 * const { currentPage, sortOrder, changePage, sortData } = useTableState({
 *   fetchAction: getSport,
 * });
 *
 * // In pagination component:
 * <Pagination
 *   current={currentPage + 1}
 *   onChange={changePage}
 * />
 *
 * // In sortable header:
 * <th onClick={() => sortData('fieldName')}>Field Name</th>
 */
export function useTableState(options: UseTableStateOptions): UseTableStateReturn {
  const {
    fetchAction,
    initialPage = 0,
    additionalParams = {},
    autoFetch = true,
  } = options;

  const dispatch = useAppDispatch();

  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [sortValue, setSortValue] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<number | ''>('');

  // Fetch data on mount if autoFetch is enabled
  useEffect(() => {
    if (autoFetch) {
      dispatch(fetchAction({
        page: initialPage,
        sortValue: '',
        sortOrder: '',
        ...additionalParams,
      }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changePage = useCallback((page: number, _pageSize?: number) => {
    const newPage = page - 1;
    setCurrentPage(newPage);
    dispatch(fetchAction({
      page: newPage,
      sortValue,
      sortOrder,
      ...additionalParams,
    }));
  }, [dispatch, fetchAction, sortValue, sortOrder, additionalParams]);

  const sortData = useCallback((sortVal: string) => {
    const newSortOrder = sortOrder === -1 ? 1 : -1;
    setSortValue(sortVal);
    setSortOrder(newSortOrder);
    dispatch(fetchAction({
      page: currentPage,
      sortValue: sortVal,
      sortOrder: newSortOrder,
      ...additionalParams,
    }));
  }, [dispatch, fetchAction, currentPage, sortOrder, additionalParams]);

  const refreshData = useCallback(() => {
    dispatch(fetchAction({
      page: currentPage,
      sortValue,
      sortOrder,
      ...additionalParams,
    }));
  }, [dispatch, fetchAction, currentPage, sortValue, sortOrder, additionalParams]);

  return {
    currentPage,
    sortValue,
    sortOrder,
    changePage,
    sortData,
    refreshData,
    setCurrentPage,
  };
}

export default useTableState;
